import { DateTime } from "luxon"

import { type OpeningHour } from "@/utils/opening_hours"

import { RESERVATION_DURATION_STEP_SECONDS } from "./reservation_duration_options"
import {
  DEFAULT_SIDEBAR_TABLE_MAX_CAPACITY,
  DEFAULT_SIDEBAR_TABLE_MIN_CAPACITY,
  getSidebarOpeningRange,
  type SidebarTableBusyBlock,
} from "./reservation_sidebar_availability"

const BOOKING_THRESHOLD_STEP_MINUTES = 15

export type AvailabilityInterval = { start: DateTime; end: DateTime }

export type AvailabilityTable = {
  id: string
  minCapacity?: number | null
  maxCapacity?: number | null
}

/** Party fits between the table's declared min/max capacity (nullable bounds fall back to defaults). */
export const matchesTableCapacity = (
  table: { minCapacity?: number | null; maxCapacity?: number | null },
  partySize: number,
): boolean => {
  const minCapacity = table.minCapacity ?? DEFAULT_SIDEBAR_TABLE_MIN_CAPACITY
  const maxCapacity = table.maxCapacity ?? DEFAULT_SIDEBAR_TABLE_MAX_CAPACITY

  return partySize >= minCapacity && partySize <= maxCapacity
}

/** Round a moment up to the next 15-minute booking slot (shared min-advance threshold behaviour). */
export const getRoundedBookingThreshold = (value: DateTime): DateTime => {
  const totalMinutes = value.minute + value.second / 60 + value.millisecond / 60000
  const roundedMinutes = Math.ceil(totalMinutes / BOOKING_THRESHOLD_STEP_MINUTES) * BOOKING_THRESHOLD_STEP_MINUTES

  return value.startOf("hour").plus({ minutes: roundedMinutes }).set({ second: 0, millisecond: 0 })
}

/** Group flat per-table busy blocks into `{ [tableId]: Interval[] }`, parsing ISO timestamps once. */
export const groupBusyBlocksByTable = (
  busyBlocks: SidebarTableBusyBlock[] = [],
): Record<string, AvailabilityInterval[]> =>
  busyBlocks.reduce<Record<string, AvailabilityInterval[]>>((acc, block) => {
    ;(acc[block.tableId] ||= []).push({
      start: DateTime.fromISO(block.startTime),
      end: DateTime.fromISO(block.endTime),
    })
    return acc
  }, {})

/**
 * Free gaps within [lower, upper], with the table's busy blocks (clipped to the range) removed.
 * Busy blocks may arrive unsorted and may overlap; both are handled.
 */
export const findFreeGaps = (
  lower: DateTime,
  upper: DateTime,
  busy: AvailabilityInterval[],
): AvailabilityInterval[] => {
  if (upper <= lower) return []

  const clipped = busy
    .map((block) => ({
      start: block.start < lower ? lower : block.start,
      end: block.end > upper ? upper : block.end,
    }))
    .filter((block) => block.end > lower && block.start < upper && block.end > block.start)
    .sort((a, b) => a.start.toMillis() - b.start.toMillis())

  const gaps: AvailabilityInterval[] = []
  let cursor = lower

  for (const block of clipped) {
    if (block.start > cursor) {
      gaps.push({ start: cursor, end: block.start })
    }
    if (block.end > cursor) {
      cursor = block.end
    }
  }

  if (cursor < upper) {
    gaps.push({ start: cursor, end: upper })
  }

  return gaps
}

/** A concrete [start, end] window is free when no busy block overlaps it. */
export const isTableFreeForRange = ({
  busyBlocks,
  start,
  end,
}: {
  busyBlocks: AvailabilityInterval[]
  start: DateTime
  end: DateTime
}): boolean => !busyBlocks.some((block) => block.start < end && block.end > start)

/** The table has at least one contiguous free window of `minDurationSeconds` inside `bounds`. */
export const hasFreeWindow = ({
  busyBlocks,
  bounds,
  minDurationSeconds,
}: {
  busyBlocks: AvailabilityInterval[]
  bounds: AvailabilityInterval
  minDurationSeconds: number
}): boolean =>
  findFreeGaps(bounds.start, bounds.end, busyBlocks).some(
    (gap) => gap.end.diff(gap.start, "seconds").seconds >= minDurationSeconds,
  )

/**
 * Latest end a reservation starting at `start` can have for a table with these busy blocks: the
 * closing time, capped at the start of the next busy block after `start`. Returns null when `start`
 * is at/after closing or sits inside a busy block (no bookable window there). Drives the mobile
 * start/end time picker's disabled rows.
 */
export const getReservationWindowEnd = (
  start: DateTime,
  busyBlocks: AvailabilityInterval[],
  closesAt: DateTime,
): DateTime | null => {
  if (start >= closesAt) return null

  let windowEnd = closesAt
  for (const block of busyBlocks) {
    if (block.start <= start && block.end > start) return null
    if (block.start > start && block.start < windowEnd) windowEnd = block.start
  }

  return windowEnd
}

/**
 * A start slot is bookable when it clears the min-advance threshold (`earliestStart`, if any) and
 * leaves at least `minDurationSeconds` of free room before the closing time / next busy block.
 */
export const isReservationStartEnabled = ({
  start,
  busyBlocks,
  closesAt,
  earliestStart,
  minDurationSeconds,
}: {
  start: DateTime
  busyBlocks: AvailabilityInterval[]
  closesAt: DateTime
  earliestStart: DateTime | null
  minDurationSeconds: number
}): boolean => {
  if (earliestStart && start < earliestStart) return false

  const windowEnd = getReservationWindowEnd(start, busyBlocks, closesAt)
  if (!windowEnd) return false

  return windowEnd.diff(start, "seconds").seconds >= minDurationSeconds
}

/**
 * An end slot is bookable for `referenceStart` when it lands in
 * [start + minDuration, min(start + maxDuration, closing, next busy block after start)]. A null
 * `referenceStart` (no start chosen and none valid) leaves the whole column disabled.
 */
export const isReservationEndEnabled = ({
  end,
  referenceStart,
  busyBlocks,
  closesAt,
  minDurationSeconds,
  maxDurationSeconds,
}: {
  end: DateTime
  referenceStart: DateTime | null
  busyBlocks: AvailabilityInterval[]
  closesAt: DateTime
  minDurationSeconds: number
  maxDurationSeconds?: number | null
}): boolean => {
  if (!referenceStart) return false

  const windowEnd = getReservationWindowEnd(referenceStart, busyBlocks, closesAt)
  if (!windowEnd) return false

  const lower = referenceStart.plus({ seconds: minDurationSeconds })
  let upper = windowEnd
  if (maxDurationSeconds && maxDurationSeconds > 0) {
    const maxEnd = referenceStart.plus({ seconds: maxDurationSeconds })
    if (maxEnd < upper) upper = maxEnd
  }

  return end >= lower && end <= upper
}

type AvailableTableIdsInput = {
  tables: AvailabilityTable[]
  partySize: number
  date: DateTime | null
  openingHours?: OpeningHour[]
  timezone: string
  busyBlocks?: SidebarTableBusyBlock[]
  startTime?: string | null
  endTime?: string | null
  minReservationTime?: number | null
  minAdvanceBookingTime?: number | null
}

/**
 * Set of table ids that are bookable for the current party + date, driving the map colours, the
 * "{N} available" count and the table-dropdown enabled rows on the mobile table screen.
 *
 * A table is available when its capacity fits the party AND:
 *  - if a concrete [startTime, endTime] window is selected, that window is free (no busy overlap);
 *  - otherwise it has at least one free window of `minReservationTime` inside opening hours,
 *    after the min-advance booking threshold.
 */
export const getAvailableTableIds = ({
  tables,
  partySize,
  date,
  openingHours,
  timezone,
  busyBlocks = [],
  startTime,
  endTime,
  minReservationTime,
  minAdvanceBookingTime,
}: AvailableTableIdsInput): Set<string> => {
  const openingRange = getSidebarOpeningRange(date, openingHours, timezone)
  if (!date || !openingRange || partySize <= 0) return new Set()

  const minDurationSeconds =
    minReservationTime && minReservationTime > 0 ? minReservationTime : RESERVATION_DURATION_STEP_SECONDS

  const bookingThreshold = minAdvanceBookingTime
    ? getRoundedBookingThreshold(DateTime.now().setZone(timezone).plus({ seconds: minAdvanceBookingTime }))
    : DateTime.now().setZone(timezone)

  const bounds: AvailabilityInterval = {
    start: bookingThreshold > openingRange.opensAt ? bookingThreshold : openingRange.opensAt,
    end: openingRange.closesAt,
  }

  const busyByTable = groupBusyBlocksByTable(busyBlocks)

  const rangeStart = startTime ? DateTime.fromISO(startTime) : null
  const rangeEnd = endTime ? DateTime.fromISO(endTime) : null
  const hasRange = Boolean(rangeStart?.isValid && rangeEnd?.isValid)

  const available = new Set<string>()

  tables.forEach((table) => {
    if (!matchesTableCapacity(table, partySize)) return

    const busy = busyByTable[table.id] || []

    const isAvailable =
      hasRange && rangeStart && rangeEnd
        ? isTableFreeForRange({ busyBlocks: busy, start: rangeStart, end: rangeEnd })
        : hasFreeWindow({ busyBlocks: busy, bounds, minDurationSeconds })

    if (isAvailable) available.add(table.id)
  })

  return available
}
